import { Component, OnInit } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  AGENT_PERMISSIONS,
  LlmModel,
  PLAN_DEFAULTS,
  PLAN_FEATURES,
  PLAN_LIMITS,
  PlanFeatureKey,
  PlanLimitKey,
  ProjectOption,
  ProjectPlan,
  ProjectPlanRequest,
  Provider,
  withPlanDefaults,
} from '../../models/ai-agents.models';
import { AgentPermissionsService } from '../../services/agent-permissions.service';
import { AiAgentsApiService } from '../../services/ai-agents-api.service';

export interface PlanRow {
  projectId: string;
  projectName: string;
  plan: ProjectPlan;
  /** true when the project has no stored plan and runs on the defaults. */
  isDefault: boolean;
}

export interface PlanModelGroup {
  label: string;
  models: { id: string; label: string; enabled: boolean }[];
}

export function defaultPlan(projectId: string): ProjectPlan {
  return {
    projectId,
    maxAgents: PLAN_DEFAULTS.maxAgents,
    maxMessagesPerMonth: PLAN_DEFAULTS.maxMessagesPerMonth,
    allowedModelIds: null,
    enabled: true,
    updatedAt: null,
    allowVoiceInput: PLAN_DEFAULTS.allowVoiceInput,
    allowVoiceOutput: PLAN_DEFAULTS.allowVoiceOutput,
    allowImages: PLAN_DEFAULTS.allowImages,
    allowKnowledgeFiles: PLAN_DEFAULTS.allowKnowledgeFiles,
    allowDocuments: PLAN_DEFAULTS.allowDocuments,
    allowPlaybooks: PLAN_DEFAULTS.allowPlaybooks,
    maxKnowledgeFiles: PLAN_DEFAULTS.maxKnowledgeFiles,
    maxKnowledgeMb: PLAN_DEFAULTS.maxKnowledgeMb,
    maxAudioSecondsPerMonth: PLAN_DEFAULTS.maxAudioSecondsPerMonth,
    maxPlaybooksPerAgent: PLAN_DEFAULTS.maxPlaybooksPerAgent,
    allowTools: PLAN_DEFAULTS.allowTools,
    maxToolConnectionsPerAgent: PLAN_DEFAULTS.maxToolConnectionsPerAgent,
    allowTriggers: PLAN_DEFAULTS.allowTriggers,
    maxTriggersPerAgent: PLAN_DEFAULTS.maxTriggersPerAgent,
    maxTriggerRunsPerMonth: PLAN_DEFAULTS.maxTriggerRunsPerMonth,
  };
}

/** "Images, Voice input" or "None" for the features a plan allows. */
export function planFeaturesLabel(plan: ProjectPlan): string {
  const on = PLAN_FEATURES.filter((f) => plan[f.key] === true).map((f) => f.label);
  return on.length ? on.join(', ') : 'None';
}

/** Every known project with its plan; a project without a stored plan shows the defaults. */
export function buildPlanRows(plans: ProjectPlan[], projects: ProjectOption[]): PlanRow[] {
  const byId = new Map(plans.map((p) => [p.projectId, withPlanDefaults(p)]));
  const rows: PlanRow[] = [];
  for (const pr of projects) {
    const stored = byId.get(pr.projectId);
    rows.push({
      projectId: pr.projectId,
      projectName: pr.name || pr.projectId,
      plan: stored || defaultPlan(pr.projectId),
      isDefault: !stored,
    });
    byId.delete(pr.projectId);
  }
  byId.forEach((p) => rows.push({ projectId: p.projectId, projectName: p.projectId, plan: p, isDefault: false }));
  return rows.sort((a, b) => a.projectName.localeCompare(b.projectName));
}

/** Models grouped by provider for the allowed-models picker. Disabled models stay listed only when already selected. */
export function buildPlanModelGroups(models: LlmModel[], providers: Provider[], selected: string[] = []): PlanModelGroup[] {
  const groups = new Map<string, PlanModelGroup>();
  for (const m of models) {
    if (!m.enabled && !selected.includes(m.id)) continue;
    let g = groups.get(m.providerId);
    if (!g) {
      const p = providers.find((x) => x.id === m.providerId);
      g = { label: p ? `${p.displayName}${p.enabled ? '' : ' (provider disabled)'}` : m.providerId, models: [] };
      groups.set(m.providerId, g);
    }
    g.models.push({ id: m.id, label: `${m.displayName} (${m.modelId})${m.enabled ? '' : ' - disabled'}`, enabled: m.enabled });
  }
  return Array.from(groups.values()).sort((a, b) => a.label.localeCompare(b.label));
}

export interface PlanForm {
  maxAgents: number | null;
  maxMessagesPerMonth: number | null;
  allModels: boolean;
  selected: Set<string>;
  enabled: boolean;
  allowVoiceInput: boolean;
  allowVoiceOutput: boolean;
  allowImages: boolean;
  allowKnowledgeFiles: boolean;
  allowDocuments: boolean;
  /** Contract v1.4 */
  allowPlaybooks: boolean;
  maxKnowledgeFiles: number | null;
  maxKnowledgeMb: number | null;
  maxAudioSecondsPerMonth: number | null;
  /** Contract v1.4 */
  maxPlaybooksPerAgent: number | null;
  /** Contract v1.5 */
  allowTools: boolean;
  /** Contract v1.5 */
  maxToolConnectionsPerAgent: number | null;
  /** Contract v1.6 */
  allowTriggers: boolean;
  /** Contract v1.6 */
  maxTriggersPerAgent: number | null;
  /** Contract v1.6 */
  maxTriggerRunsPerMonth: number | null;
}

/** The request of a plan form, or the error to show. Every number must be a whole number, 0 or more. */
export function buildPlanRequest(form: PlanForm): { body: ProjectPlanRequest | null; error: string } {
  const maxAgents = toInt(form.maxAgents);
  const maxMessages = toInt(form.maxMessagesPerMonth);
  if (maxAgents === null || maxAgents < 0 || maxMessages === null || maxMessages < 0) {
    return { body: null, error: 'Max agents and max messages per month must be whole numbers, 0 or more.' };
  }
  const limits: Partial<Record<PlanLimitKey, number>> = {};
  for (const l of PLAN_LIMITS) {
    const n = toInt(form[l.key]);
    if (n === null || n < 0) {
      return { body: null, error: l.label + ' must be a whole number, 0 or more.' };
    }
    limits[l.key] = n;
  }
  if (!form.allModels && form.selected.size === 0) {
    return { body: null, error: 'Select at least one model, or choose "All enabled models".' };
  }
  return {
    error: '',
    body: {
      maxAgents,
      maxMessagesPerMonth: maxMessages,
      allowedModelIds: form.allModels ? null : Array.from(form.selected),
      enabled: form.enabled,
      allowVoiceInput: form.allowVoiceInput,
      allowVoiceOutput: form.allowVoiceOutput,
      allowImages: form.allowImages,
      allowKnowledgeFiles: form.allowKnowledgeFiles,
      allowDocuments: form.allowDocuments,
      allowPlaybooks: form.allowPlaybooks,
      maxKnowledgeFiles: limits.maxKnowledgeFiles as number,
      maxKnowledgeMb: limits.maxKnowledgeMb as number,
      maxAudioSecondsPerMonth: limits.maxAudioSecondsPerMonth as number,
      maxPlaybooksPerAgent: limits.maxPlaybooksPerAgent as number,
      allowTools: form.allowTools,
      maxToolConnectionsPerAgent: limits.maxToolConnectionsPerAgent as number,
      allowTriggers: form.allowTriggers,
      maxTriggersPerAgent: limits.maxTriggersPerAgent as number,
      maxTriggerRunsPerMonth: limits.maxTriggerRunsPerMonth as number,
    },
  };
}

@Component({
  selector: 'app-ai-project-plans-page',
  templateUrl: './project-plans-page.component.html',
  styleUrls: ['../../ai-agents.shared.css'],
})
export class ProjectPlansPageComponent implements OnInit {
  readonly P = AGENT_PERMISSIONS;
  readonly defaults = PLAN_DEFAULTS;
  readonly features = PLAN_FEATURES;
  readonly limits = PLAN_LIMITS;
  readonly featuresLabel = planFeaturesLabel;

  rows: PlanRow[] = [];
  models: LlmModel[] = [];
  providers: Provider[] = [];
  search = '';
  loading = false;
  loadError = '';

  editing: PlanRow | null = null;
  form: PlanForm = this.blankForm();
  modelGroups: PlanModelGroup[] = [];
  formError = '';
  saving = false;

  constructor(private api: AiAgentsApiService, public perms: AgentPermissionsService) {}

  ngOnInit(): void {
    if (this.perms.has(this.P.modelList)) {
      this.api.listModels().subscribe({ next: (list) => (this.models = list || []) });
    }
    if (this.perms.has(this.P.providerList)) {
      this.api.listProviders().subscribe({ next: (list) => (this.providers = list || []) });
    }
    this.load();
  }

  get filtered(): PlanRow[] {
    const q = this.search.trim().toLowerCase();
    if (!q) return this.rows;
    return this.rows.filter((r) => r.projectName.toLowerCase().includes(q) || r.projectId.toLowerCase().includes(q));
  }

  load(): void {
    this.loading = true;
    this.loadError = '';
    forkJoin({
      plans: this.api.listProjectPlans(),
      projects: this.api.listProjects().pipe(catchError(() => of([] as ProjectOption[]))),
    }).subscribe({
      next: ({ plans, projects }) => {
        this.rows = buildPlanRows(plans || [], projects || []);
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.loadError = err?.error?.message || 'Loading the plans failed.';
      },
    });
  }

  modelsLabel(plan: ProjectPlan): string {
    if (plan.allowedModelIds === null || plan.allowedModelIds === undefined) return 'All enabled models';
    if (plan.allowedModelIds.length === 0) return 'No models';
    const names = plan.allowedModelIds.map((id) => this.models.find((m) => m.id === id)?.displayName || id);
    return names.length <= 3 ? names.join(', ') : `${names.slice(0, 3).join(', ')} +${names.length - 3}`;
  }

  openEdit(row: PlanRow): void {
    this.editing = row;
    this.formError = '';
    this.fillForm(row.plan);
    if (!row.isDefault && this.perms.has(this.P.planGet)) {
      this.api.getProjectPlan(row.projectId).subscribe({
        next: (plan) => {
          if (plan && this.editing === row) this.fillForm(plan);
        },
      });
    }
  }

  closeEditor(): void {
    this.editing = null;
  }

  toggleModel(id: string, checked: boolean): void {
    if (checked) this.form.selected.add(id);
    else this.form.selected.delete(id);
  }

  save(): void {
    if (!this.editing) return;
    const built = buildPlanRequest(this.form);
    if (!built.body) {
      this.formError = built.error;
      return;
    }
    const body: ProjectPlanRequest = built.body;
    const row = this.editing;
    this.saving = true;
    this.api.updateProjectPlan(row.projectId, body).subscribe({
      next: (saved) => {
        this.saving = false;
        const plan: ProjectPlan = withPlanDefaults(saved && saved.projectId ? saved : { ...row.plan, ...body });
        this.rows = this.rows.map((r) => (r.projectId === row.projectId ? { ...r, plan, isDefault: false } : r));
        this.editing = null;
      },
      error: (err) => {
        this.saving = false;
        this.formError = err?.error?.message || 'Saving failed.';
      },
    });
  }

  setFeature(key: PlanFeatureKey, on: boolean): void {
    this.form[key] = on;
  }

  private fillForm(stored: ProjectPlan): void {
    const plan = withPlanDefaults(stored);
    const selected = plan.allowedModelIds ? [...plan.allowedModelIds] : [];
    this.form = {
      maxAgents: plan.maxAgents,
      maxMessagesPerMonth: plan.maxMessagesPerMonth,
      allModels: plan.allowedModelIds === null || plan.allowedModelIds === undefined,
      selected: new Set(selected),
      enabled: plan.enabled,
      allowVoiceInput: plan.allowVoiceInput === true,
      allowVoiceOutput: plan.allowVoiceOutput === true,
      allowImages: plan.allowImages === true,
      allowKnowledgeFiles: plan.allowKnowledgeFiles === true,
      allowDocuments: plan.allowDocuments === true,
      allowPlaybooks: plan.allowPlaybooks === true,
      maxKnowledgeFiles: plan.maxKnowledgeFiles ?? PLAN_DEFAULTS.maxKnowledgeFiles,
      maxKnowledgeMb: plan.maxKnowledgeMb ?? PLAN_DEFAULTS.maxKnowledgeMb,
      maxAudioSecondsPerMonth: plan.maxAudioSecondsPerMonth ?? PLAN_DEFAULTS.maxAudioSecondsPerMonth,
      maxPlaybooksPerAgent: plan.maxPlaybooksPerAgent ?? PLAN_DEFAULTS.maxPlaybooksPerAgent,
      allowTools: plan.allowTools === true,
      maxToolConnectionsPerAgent: plan.maxToolConnectionsPerAgent ?? PLAN_DEFAULTS.maxToolConnectionsPerAgent,
      allowTriggers: plan.allowTriggers === true,
      maxTriggersPerAgent: plan.maxTriggersPerAgent ?? PLAN_DEFAULTS.maxTriggersPerAgent,
      maxTriggerRunsPerMonth: plan.maxTriggerRunsPerMonth ?? PLAN_DEFAULTS.maxTriggerRunsPerMonth,
    };
    this.modelGroups = buildPlanModelGroups(this.models, this.providers, selected);
  }

  private blankForm(): PlanForm {
    return {
      maxAgents: PLAN_DEFAULTS.maxAgents,
      maxMessagesPerMonth: PLAN_DEFAULTS.maxMessagesPerMonth,
      allModels: true,
      selected: new Set<string>(),
      enabled: true,
      allowVoiceInput: PLAN_DEFAULTS.allowVoiceInput,
      allowVoiceOutput: PLAN_DEFAULTS.allowVoiceOutput,
      allowImages: PLAN_DEFAULTS.allowImages,
      allowKnowledgeFiles: PLAN_DEFAULTS.allowKnowledgeFiles,
      allowDocuments: PLAN_DEFAULTS.allowDocuments,
      allowPlaybooks: PLAN_DEFAULTS.allowPlaybooks,
      maxKnowledgeFiles: PLAN_DEFAULTS.maxKnowledgeFiles,
      maxKnowledgeMb: PLAN_DEFAULTS.maxKnowledgeMb,
      maxAudioSecondsPerMonth: PLAN_DEFAULTS.maxAudioSecondsPerMonth,
      maxPlaybooksPerAgent: PLAN_DEFAULTS.maxPlaybooksPerAgent,
      allowTools: PLAN_DEFAULTS.allowTools,
      maxToolConnectionsPerAgent: PLAN_DEFAULTS.maxToolConnectionsPerAgent,
      allowTriggers: PLAN_DEFAULTS.allowTriggers,
      maxTriggersPerAgent: PLAN_DEFAULTS.maxTriggersPerAgent,
      maxTriggerRunsPerMonth: PLAN_DEFAULTS.maxTriggerRunsPerMonth,
    };
  }
}

function toInt(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isInteger(n) ? n : null;
}
